import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from './i18n/LanguageContext.js';
import { submitContact, getContactMessages } from './commerce-api.js';
import './customer-support.css';

const COPY = {
  en: { button: 'Contact us', title: 'How can we help?', intro: 'Ask about your order, sizing or styles. Our team’s replies will appear here.', login: 'Sign in to message us', placeholder: 'Type your message…', message: 'Your message', send: 'Send message', busy: 'Sending…', close: 'Close', error: 'Your message could not be sent. Please try again.', limited: 'Too many messages. Please try again later.', loading: 'Loading messages…', loadError: 'Messages could not be refreshed. Your draft is still here.', retry: 'Try again', refresh: 'Refresh messages', empty: 'Send your first message. We’ll get back to you here.', you: 'You', conversation: 'Your conversation with MINI MURAL' },
  zh: { button: '联系客服', title: '有什么可以帮你？', intro: '订单、尺寸或款式问题，都可以在这里告诉我们。客服回复会显示在此处。', login: '登录后开始对话', placeholder: '请输入消息…', message: '留言内容', send: '提交留言', busy: '正在提交…', close: '关闭', error: '留言未能提交，请稍后重试。', limited: '提交过于频繁，请稍后再试。', loading: '正在加载消息…', loadError: '消息刷新失败，已保留你正在输入的内容。', retry: '重试', refresh: '刷新消息', empty: '发送第一条消息，客服将在这里回复你。', you: '你', conversation: '你与 MINI MURAL 的对话' },
  fr: { button: 'Nous contacter', title: 'Comment vous aider ?', intro: 'Écrivez-nous au sujet de votre commande, des tailles ou des styles. Les réponses de notre équipe apparaîtront ici.', login: 'Se connecter pour écrire', placeholder: 'Votre message…', message: 'Votre message', send: 'Envoyer', busy: 'Envoi…', close: 'Fermer', error: 'Envoi impossible. Veuillez réessayer.', limited: 'Trop de messages. Réessayez plus tard.', loading: 'Chargement des messages…', loadError: 'Impossible d’actualiser les messages. Votre brouillon est conservé.', retry: 'Réessayer', refresh: 'Actualiser les messages', empty: 'Envoyez votre premier message. Nous vous répondrons ici.', you: 'Vous', conversation: 'Votre conversation avec MINI MURAL' },
  de: { button: 'Kontakt', title: 'Wie können wir helfen?', intro: 'Schreiben Sie uns zu Ihrer Bestellung, Größen oder Designs. Die Antworten unseres Teams erscheinen hier.', login: 'Zum Schreiben anmelden', placeholder: 'Nachricht eingeben…', message: 'Ihre Nachricht', send: 'Nachricht senden', busy: 'Wird gesendet…', close: 'Schließen', error: 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.', limited: 'Zu viele Nachrichten. Bitte versuchen Sie es später erneut.', loading: 'Nachrichten werden geladen…', loadError: 'Nachrichten konnten nicht aktualisiert werden. Ihr Entwurf bleibt erhalten.', retry: 'Erneut versuchen', refresh: 'Nachrichten aktualisieren', empty: 'Senden Sie Ihre erste Nachricht. Wir antworten Ihnen hier.', you: 'Sie', conversation: 'Ihr Gespräch mit MINI MURAL' },
  es: { button: 'Contactar', title: '¿Cómo podemos ayudarte?', intro: 'Escríbenos sobre tu pedido, las tallas o los estilos. Las respuestas de nuestro equipo aparecerán aquí.', login: 'Inicia sesión para escribir', placeholder: 'Tu mensaje…', message: 'Tu mensaje', send: 'Enviar mensaje', busy: 'Enviando…', close: 'Cerrar', error: 'No se pudo enviar. Inténtalo de nuevo.', limited: 'Demasiados mensajes. Inténtalo más tarde.', loading: 'Cargando mensajes…', loadError: 'No se pudieron actualizar los mensajes. Tu borrador sigue aquí.', retry: 'Reintentar', refresh: 'Actualizar mensajes', empty: 'Envía tu primer mensaje. Te responderemos aquí.', you: 'Tú', conversation: 'Tu conversación con MINI MURAL' },
};

function conversationEvents(items) {
  const events = [];
  for (const item of items) {
    events.push({ ...item, key: `customer-${item.id}`, kind: 'customer' });
    for (const reply of Array.isArray(item.replies) ? item.replies : []) {
      events.push({ ...reply, key: `reply-${item.id}-${reply.id}`, kind: 'support' });
    }
  }
  // A reply to an earlier inquiry still belongs after newer customer messages.
  return events.sort((a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0));
}

export default function CustomerSupport({ user, onLogin }) {
  // Remount on every identity change: no old conversation or draft can render for
  // another customer, even before effect cleanup or after a delayed response.
  const identity = user ? `user:${user.id ?? user.email}` : 'guest';
  return <SupportDialog key={identity} user={user} onLogin={onLogin} />;
}

function SupportDialog({ user, onLogin }) {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.en;
  const dialog = useRef(null);
  const launcher = useRef(null);
  const messages = useRef(null);
  const pinnedToBottom = useRef(true);
  const mounted = useRef(true);
  const readRequest = useRef(null);
  const writeRequest = useRef(null);
  const readRevision = useRef(0);
  const sending = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const events = useMemo(() => conversationEvents(items), [items]);
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : language, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }), [language]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      readRevision.current += 1;
      readRequest.current?.abort();
      writeRequest.current?.abort();
    };
  }, []);

  useEffect(() => { setRequiresLogin(false); }, [user]);

  function requireLogin() {
    setRequiresLogin(true);
    setItems([]);
    setDraft('');
    setLoadFailed(false);
    setSendError('');
  }

  const refresh = useCallback(async () => {
    if (!user || requiresLogin || sending.current || readRequest.current || !dialog.current?.open || document.visibilityState !== 'visible') return;
    const controller = new AbortController();
    const revision = ++readRevision.current;
    readRequest.current = controller;
    setLoading(true);
    try {
      const result = await getContactMessages(controller.signal);
      if (!Array.isArray(result.items)) throw new Error('Invalid conversation response');
      if (mounted.current && revision === readRevision.current) {
        setItems(result.items);
        setLoaded(true);
        setLoadFailed(false);
      }
    } catch (error) {
      if (mounted.current && revision === readRevision.current && error.name !== 'AbortError') {
        if (error.status === 401) requireLogin();
        else setLoadFailed(true);
      }
    } finally {
      if (readRequest.current === controller) readRequest.current = null;
      if (mounted.current && revision === readRevision.current) setLoading(false);
    }
  }, [user, requiresLogin]);

  useEffect(() => {
    if (!isOpen || !user || requiresLogin) return;
    refresh();
    const interval = window.setInterval(refresh, 12000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
      readRevision.current += 1;
      readRequest.current?.abort();
      readRequest.current = null;
    };
  }, [isOpen, user, requiresLogin, refresh]);

  useEffect(() => {
    if (isOpen && pinnedToBottom.current && messages.current) messages.current.scrollTop = messages.current.scrollHeight;
  }, [events, isOpen]);

  function close() {
    if (sending.current) return;
    dialog.current.close();
    setIsOpen(false);
    launcher.current?.focus();
  }

  function open() {
    if (dialog.current.open) return;
    setSendError('');
    pinnedToBottom.current = true;
    dialog.current.showModal();
    setIsOpen(true);
  }

  async function submit(event) {
    event.preventDefault();
    if (sending.current || !draft.trim() || !user || requiresLogin) return;
    sending.current = true;
    readRevision.current += 1;
    readRequest.current?.abort();
    readRequest.current = null;
    const controller = new AbortController();
    writeRequest.current = controller;
    setBusy(true);
    setLoading(false);
    setSendError('');
    let unauthorized = false;
    try {
      const result = await submitContact({ message: draft.trim(), mode: 'conversation' }, controller.signal);
      if (!result.item || typeof result.item.message !== 'string') throw new Error('Invalid message response');
      if (mounted.current) {
        pinnedToBottom.current = true;
        setItems(rows => [...rows.filter(row => row.id !== result.item.id), result.item]);
        setDraft('');
      }
    } catch (error) {
      if (mounted.current && error.name !== 'AbortError') {
        unauthorized = error.status === 401;
        if (unauthorized) requireLogin();
        else setSendError(error.status === 429 ? 'limited' : 'error');
      }
    } finally {
      sending.current = false;
      writeRequest.current = null;
      if (mounted.current) {
        setBusy(false);
        if (!unauthorized) refresh();
      }
    }
  }

  return <div className="customer-support" translate="no" data-language-owned="true">
    <button ref={launcher} className="customer-support-launcher" type="button" aria-label={copy.button} aria-haspopup="dialog" onClick={open}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H4l-2 2V11.5a9.5 9.5 0 1 1 19 0Z"/><path d="M7 10h9M7 14h6"/></svg>
      <span>{copy.button}</span>
    </button>
    <dialog ref={dialog} className="customer-support-dialog" aria-labelledby="customer-support-title" onClose={() => setIsOpen(false)} onCancel={event => { event.preventDefault(); close(); }}>
      <div className="customer-support-heading"><h2 id="customer-support-title">{copy.title}</h2><button type="button" onClick={close} disabled={busy} aria-label={copy.close}>×</button></div>
      <p>{copy.intro}</p>
      {!user || requiresLogin ? <button className="customer-support-submit" onClick={() => { close(); onLogin(); }}>{copy.login}</button> : <>
        <div className="customer-support-refresh"><button type="button" onClick={refresh} disabled={loading || busy}>{copy.refresh}</button></div>
        <div ref={messages} className="customer-support-messages" role="log" aria-label={copy.conversation} aria-live="polite" aria-relevant="additions text" onScroll={() => { const node = messages.current; pinnedToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 40; }}>
          {loading && !loaded && <p className="customer-support-empty" role="status">{copy.loading}</p>}
          {loaded && !events.length && <p className="customer-support-empty">{copy.empty}</p>}
          {events.map(item => <div className={`customer-support-bubble customer-support-bubble--${item.kind}`} key={item.key}>
            <span>{item.message}</span>
            <small><strong>{item.kind === 'customer' ? copy.you : 'MINI MURAL'}</strong>{Number.isFinite(Date.parse(item.createdAt)) && <time dateTime={item.createdAt}>{dateFormat.format(new Date(item.createdAt))}</time>}</small>
          </div>)}
        </div>
        {loadFailed && <div className="customer-support-load-error"><p role="alert">{copy.loadError}</p><button type="button" onClick={refresh} disabled={loading || busy}>{copy.retry}</button></div>}
        <form onSubmit={submit}>
          <textarea aria-label={copy.message} placeholder={copy.placeholder} value={draft} onChange={event => setDraft(event.target.value)} required maxLength={4000} rows={2} disabled={busy}/>
          {sendError && <p role="alert" className="customer-support-error">{copy[sendError]}</p>}
          <button className="customer-support-submit" type="submit" disabled={busy || !draft.trim()}>{busy ? copy.busy : copy.send}</button>
        </form>
      </>}
    </dialog>
  </div>;
}
